import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import { apiGet } from "../../shared/apiClient";
import { deleteProductVarientOption, fetchProductVarientOptionsList } from "../../shared/productChildMastersApi";
import { IconPlus } from "../../shared/toolbarIcons";
import { mastersDataGridSx } from "../../shared/mastersDataGridSx";
import { DEFAULT_PAGE_SIZE_OPTIONS } from "../../shared/useClientPagination";

export function ProductVarientOptionsList() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [varients, setVarients] = useState([]);
  const [attributes, setAttributes] = useState([]);
  const [attributeValues, setAttributeValues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    const [iRes, vRes, aRes, avRes] = await Promise.all([
      fetchProductVarientOptionsList(),
      apiGet("/api/ProductVarients"),
      apiGet("/api/Attributes"),
      apiGet("/api/AttributeValues")
    ]);
    if (!iRes.ok) {
      setError(iRes.error ?? "Failed to load data");
      setItems([]);
    } else {
      setItems(iRes.items);
    }
    if (vRes.ok) setVarients(vRes.data);
    if (aRes.ok) setAttributes(aRes.data);
    if (avRes.ok) setAttributeValues(avRes.data);
    setLoading(false);
  };

  useEffect(() => {
    void load();
  }, []);

  const onEdit = (x) => {
    navigate(`/product-varient-options?edit=${x.id}`);
  };

  const onDelete = async (id) => {
    if (!(await window.confirmDialog("Delete this product varient option?"))) return;
    const res = await deleteProductVarientOption(id);
    if (!res.ok) setError(res.error ?? "Failed to delete product varient option");
    else await load();
  };

  const varientById = useMemo(() => {
    const map = new Map();
    for (const varient of varients) map.set(varient.id, varient);
    return map;
  }, [varients]);

  const attributeById = useMemo(() => {
    const map = new Map();
    for (const attribute of attributes) map.set(attribute.id, attribute);
    return map;
  }, [attributes]);

  const attributeValueById = useMemo(() => {
    const map = new Map();
    for (const attributeValue of attributeValues) map.set(attributeValue.id, attributeValue);
    return map;
  }, [attributeValues]);

  const rows = useMemo(
    () =>
      items.map((item) => ({
        id: item.id,
        item,
        varient: varientById.get(item.productVarientId)?.skU || `#${item.productVarientId}`,
        attribute: attributeById.get(item.attributeId)?.name || `#${item.attributeId}`,
        attributeValue: attributeValueById.get(item.attributeValueId)?.value || `#${item.attributeValueId}`
      })),
    [items, varientById, attributeById, attributeValueById]
  );

  const total = rows.length;
  const columns = [
      { field: "varient", headerName: "Varient", flex: 1, minWidth: 140 },
      { field: "attribute", headerName: "Attribute", flex: 1, minWidth: 140 },
      { field: "attributeValue", headerName: "Attribute Value", flex: 1, minWidth: 150 },
      {
        field: "actions",
        headerName: "Actions",
        width: 120,
        align: "center",
        headerAlign: "center",
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <div className="table-actions">
            <button className="icon-button" type="button" title="Edit" onClick={() => onEdit(params.row.item)}>
              ✏️
            </button>
            <button className="icon-button danger" type="button" title="Delete" onClick={() => void onDelete(params.row.item.id)}>
              🗑️
            </button>
          </div>
        )
      }
  ];

  return (
    <div className="card">
      <div className="card-header card-header--row">
        <div>
          <h2>Product Varient Options</h2>
          <p>{loading ? "Loading..." : `Total: ${total}`}</p>
        </div>
        <div className="card-header-actions">
          <Link to="/product-varient-options" className="icon-button icon-button--toolbar" title="Add varient option" aria-label="Add varient option">
            <IconPlus />
          </Link>
        </div>
      </div>
      <div className="card-body">
        {error && <div className="error-banner">{error}</div>}
        <div className="masters-data-grid-shell">
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            disableRowSelectionOnClick
            pagination
            paginationMode="client"
            pageSizeOptions={DEFAULT_PAGE_SIZE_OPTIONS}
            initialState={{
              pagination: { paginationModel: { page: 0, pageSize: DEFAULT_PAGE_SIZE_OPTIONS[0] } }
            }}
            sx={mastersDataGridSx}
          />
        </div>
      </div>
    </div>
  );
}
